import { graphql, Link, navigate } from "gatsby"
import { buildPostFromNode } from "libs/utils/blogSearch"
import { convertSlugToCategoryName, getCategorySlug } from "libs/utils/dom.ts"
import React, { useEffect, useMemo } from "react"
import styled from "styled-components"
import { Col, Container, Row } from "styleguide/components/grid"
import Header from "styleguide/components/Header"
import Layout from "styleguide/components/Layout"
import SEO from "styleguide/components/Seo"
import {
  BlogHero,
  BlogTile,
  FeaturedPost,
  WhatWeHaveDone,
} from "styleguide/page-components/Blog"
import { uiColorToBaseColor } from "styleguide/styles/colors"
import media from "styleguide/styles/media-queries"
import { StringParam, useQueryParam } from "use-query-params"

const StyledContainer = styled(Container)`
  margin-bottom: 48px;
`
const StyledCol = styled(Col)`
  margin-bottom: 64px;
  p {
    font-weight: 500;
  }
  span {
    font-size: 14px;
    font-weight: 500;
  }
`
const CategoriesBar = styled(Col)`
  margin-bottom: 64px;
  display: flex;
  flex-wrap: wrap;
  ${media.desktop`
    margin-bottom: 48px;
  `}
`
const Category = styled(Link)<{ isActive: boolean }>`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  line-height: 24px;
  color: ${props => uiColorToBaseColor(props.isActive ? "darkGray" : "gray")};
  margin-right: 22px;
  text-transform: uppercase;
  &:hover {
    text-decoration: none;
    color: ${props => uiColorToBaseColor(props.isActive ? "darkGray" : "gray")};
    cursor: pointer;
  }
  ${media.desktop`
    font-size:13px;
    letter-spacing: 0.5px;
    line-height: 16px;
  `}
`
const HorizontalLine = styled.div`
  height: 2px;
  min-width: 40px;
  border-radius: 2px;
  margin-right: 24px;
  background-color: ${uiColorToBaseColor("black")};
  ${media.desktop`
  margin-top:4px;
  `}
`
const CategoryWrapper = styled.div`
  ${media.desktop`
    margin-bottom: 16px;
    line-height:16px;
`}
`
const SectionSeparator = styled.div`
  background-color: ${uiColorToBaseColor("lightGray")};
  height: 2px;
  margin-bottom: 64px;
`

const BlogCategory = ({
  data: {
    allMdx: { edges },
  },
  pageContext: { currentCategory, allCategories },
}) => {
  const allPosts = edges.map(({ node }) => buildPostFromNode(node))

  const [searchQuery, updateSearchQuery] = useQueryParam("q", StringParam)

  useEffect(() => {
    if (searchQuery && searchQuery.length > 2) {
      navigate(`/blog/all-topics/?q=${searchQuery}`)
    }
  }, [searchQuery])

  const featuredPostEdge = useMemo(
    () =>
      edges.find(({ node }) => {
        const {featuredCategories} = node.frontmatter
        return (
          featuredCategories &&
          featuredCategories.length !== 0 &&
          featuredCategories.includes(currentCategory)
        )
      }),
    []
  )

  const posts = useMemo(() => {
    if (!featuredPostEdge) {
      return allPosts
    }
    return allPosts.filter(post => featuredPostEdge.node.id !== post.id)
  }, [featuredPostEdge, allPosts])

  return (
    <>
      <Header />
      <SEO
        title={`Blog - ${convertSlugToCategoryName(currentCategory)}`}
        description={convertSlugToCategoryName(currentCategory)}
      />
      <Layout
        header={
          <BlogHero
            searchQuery={searchQuery}
            onSearchQueryChange={q => updateSearchQuery(q)}
            category={convertSlugToCategoryName(currentCategory)}
          />
        }
        main={
          <>
            <StyledContainer>
              <Row>
                <CategoriesBar>
                  {allCategories.map(category => (
                    <CategoryWrapper key={category}>
                      <Category
                        to={getCategorySlug(category)}
                        isActive={category === currentCategory}
                      >
                        {convertSlugToCategoryName(category)}
                      </Category>
                      {category === currentCategory && <HorizontalLine />}
                    </CategoryWrapper>
                  ))}
                </CategoriesBar>
              </Row>
              {featuredPostEdge && (
                <>
                  <FeaturedPost
                    post={featuredPostEdge.node.frontmatter}
                    slug={featuredPostEdge.node.fields.slug}
                  />
                  <SectionSeparator />
                </>
              )}
              <Row>
                {posts.map(post => (
                  <StyledCol key={post.id} lg={4} md={6} sm={12} xs={12}>
                    <BlogTile
                      title={post.title}
                      excerpt={post.excerpt}
                      author={post.author}
                      date={post.date}
                      featuredImage={post.featuredImage}
                      slug={post.slug}
                      categories={post.categories}
                    />
                  </StyledCol>
                ))}
              </Row>
            </StyledContainer>
            <WhatWeHaveDone />
          </>
        }
      />
    </>
  )
}

export default BlogCategory

export const query = graphql`
  query BLOG_CATEGORY($currentCategory: [String]) {
    allMdx(
      filter: {
        frontmatter: {
          published: { eq: true }
          categories: { in: $currentCategory }
        }
      }
      sort: { frontmatter: { date: DESC } }
    ) {
      edges {
        node {
          frontmatter {
            title
            categories
            date(formatString: "D MMM YYYY")
            author
            excerpt
            featuredCategories
            featuredImage {
              childImageSharp {
                fluid(maxWidth: 544, quality: 90) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
          id
          fields {
            slug
          }
        }
      }
    }
  }
`
